import { useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  Check,
  Columns3,
  GanttChartSquare,
  List,
  Plus,
  Save,
  Table2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { FilterSelect, FiltersPopover, ListSearch } from '@/components/ui/list-filters';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Boxes } from 'lucide-react';
import { PRIORITIES } from '../constants';
import { projectMemberName } from '../select';
import {
  cyclesHooks,
  labelsHooks,
  modulesHooks,
  projectMembersHooks,
  projectViewsHooks,
  statesHooks,
  useWorkItems,
} from '../queries';
import type {
  Cycle,
  Label as LabelModel,
  Module,
  Project,
  ProjectMember,
  ProjectView,
  State,
  ViewLayout,
} from '../types';
import { WorkItemCreateDialog } from './WorkItemCreateDialog';
import { WorkItemsBoard } from './WorkItemsBoard';
import type { WorkItemFilters } from './workItemFilters';
import { DEFAULT_ORDER, ORDER_OPTIONS, type WorkItemOrder } from './workItemOrder';
import { WorkItemsCalendar } from './WorkItemsCalendar';
import { WorkItemsGantt } from './WorkItemsGantt';
import { WorkItemsRows } from './WorkItemsRows';

type Layout = ViewLayout;

const LAYOUTS: { value: Layout; icon: typeof List; label: string }[] = [
  { value: 'board', icon: Columns3, label: 'Board' },
  { value: 'list', icon: List, label: 'List' },
  { value: 'table', icon: Table2, label: 'Table' },
  { value: 'calendar', icon: CalendarDays, label: 'Calendar' },
  { value: 'gantt', icon: GanttChartSquare, label: 'Gantt' },
];

type SavedFilters = WorkItemFilters;
type FilterState = Record<'priority' | 'state' | 'assignee' | 'label' | 'module' | 'cycle', string>;

const EMPTY_FILTERS: FilterState = {
  priority: 'all', state: 'all', assignee: 'all', label: 'all', module: 'all', cycle: 'all',
};

export function WorkItemsPanel({ project }: { project: Project }) {
  const [layout, setLayout] = useState<Layout>('board');
  const [searchInput, setSearchInput] = useState('');
  const [fil, setFil] = useState<FilterState>(EMPTY_FILTERS);
  const [order, setOrder] = useState<WorkItemOrder>(DEFAULT_ORDER);
  const [addOpen, setAddOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const search = useDebouncedValue(searchInput.trim(), 300);
  // Guests: viewers are read-only; member/lead may add/move work items. Saving
  // views stays staff-only.
  const isGuest = useSession((s) => s.role) === 'guest';
  const canWrite = !isGuest || project.my_role === 'member' || project.my_role === 'lead';

  const num = (v: string) => (v === 'all' ? undefined : Number(v));
  const filters: WorkItemFilters = {
    search: search || undefined,
    priority: fil.priority === 'all' ? undefined : fil.priority,
    state: num(fil.state),
    assignee: num(fil.assignee),
    label: num(fil.label),
    module: num(fil.module),
    cycle: num(fil.cycle),
  };
  const activeFilters = Object.values(fil).filter((v) => v !== 'all').length;

  // Project-wide (unfiltered) count — drives the first-run empty state.
  const totalQuery = useWorkItems({ project: project.id, page_size: 1 });
  const isEmptyProject = totalQuery.data?.count === 0;

  const viewsQuery = projectViewsHooks.useList({ project: project.id });
  const views = useMemo(() => {
    const raw = viewsQuery.data;
    return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as ProjectView[];
  }, [viewsQuery.data]);

  function applyView(view: ProjectView) {
    setLayout(view.layout);
    const f = (view.filters ?? {}) as SavedFilters;
    setSearchInput(f.search ?? '');
    const s = (v?: number) => (v != null ? String(v) : 'all');
    setFil({
      priority: f.priority ?? 'all',
      state: s(f.state),
      assignee: s(f.assignee),
      label: s(f.label),
      module: s(f.module),
      cycle: s(f.cycle),
    });
  }

  return (
    <div className="space-y-4">
      {/* Toolbar (search / filters / views / add) is noise on an empty project —
          the empty state has its own create CTA. Show it once items exist. */}
      {isEmptyProject ? null : (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search work items…"
          />
          <WorkItemFiltersPopover
            project={project}
            fil={fil}
            setFil={setFil}
            activeFilters={activeFilters}
            onClear={() => setFil(EMPTY_FILTERS)}
          />
          <OrderByMenu order={order} setOrder={setOrder} />
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Views
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Saved views</DropdownMenuLabel>
              {views.length === 0 ? (
                <DropdownMenuItem disabled>No saved views</DropdownMenuItem>
              ) : (
                views.map((v) => (
                  <DropdownMenuItem key={v.id} onClick={() => applyView(v)}>
                    {v.name}
                    <span className="text-muted-foreground ml-auto text-xs capitalize">{v.layout}</span>
                  </DropdownMenuItem>
                ))
              )}
              {!isGuest ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setSaveOpen(true)}>
                    <Save className="size-4" /> Save current view
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="bg-muted/60 flex items-center rounded-md p-0.5">
            {LAYOUTS.map((l) => {
              const Icon = l.icon;
              return (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => setLayout(l.value)}
                  aria-label={l.label}
                  aria-pressed={layout === l.value}
                  className={cn(
                    'rounded p-1.5',
                    layout === l.value
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="size-4" />
                </button>
              );
            })}
          </div>
          {canWrite ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="size-4" /> Add work item
            </Button>
          ) : null}
        </div>
      </div>
      )}

      {isEmptyProject ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Boxes className="text-muted-foreground/30 size-16" strokeWidth={1.25} />
          <h2 className="mt-6 text-lg font-semibold">Start with your first work item.</h2>
          <p className="text-muted-foreground mt-1 max-w-sm text-sm">
            Work items are the building blocks of your project — assign owners, set priorities,
            and track progress easily.
          </p>
          {canWrite ? (
            <Button className="mt-5" onClick={() => setAddOpen(true)}>
              <Plus className="size-4" /> Create your first work item
            </Button>
          ) : null}
        </div>
      ) : layout === 'board' ? (
        <WorkItemsBoard project={project} filters={filters} order={order} />
      ) : layout === 'calendar' ? (
        <WorkItemsCalendar project={project} filters={filters} />
      ) : layout === 'gantt' ? (
        <WorkItemsGantt project={project} filters={filters} />
      ) : (
        <WorkItemsRows project={project} mode={layout} filters={filters} order={order} />
      )}

      <WorkItemCreateDialog project={project} open={addOpen} onOpenChange={setAddOpen} />
      <SaveViewDialog
        project={project}
        layout={layout}
        filters={filters}
        open={saveOpen}
        onOpenChange={setSaveOpen}
      />
    </div>
  );
}

function SaveViewDialog({
  project,
  layout,
  filters,
  open,
  onOpenChange,
}: {
  project: Project;
  layout: Layout;
  filters: SavedFilters;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = projectViewsHooks.useCreate();
  const [name, setName] = useState('');

  function submit() {
    if (!name.trim()) return;
    create.mutate(
      {
        project: project.id,
        name: name.trim(),
        layout,
        filters: filters as Record<string, unknown>,
        display: {},
      },
      {
        onSuccess: () => {
          setName('');
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save view</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          Saves the current layout (<span className="capitalize">{layout}</span>) + filters as a shared view.
        </p>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="View name"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Saving…' : 'Save view'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function rowsOf<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

function OrderByMenu({
  order,
  setOrder,
}: {
  order: WorkItemOrder;
  setOrder: (o: WorkItemOrder) => void;
}) {
  const active = ORDER_OPTIONS.find((o) => o.key === order.key);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          <ArrowUpDown className="size-4" /> Sort
          {order.key !== 'manual' ? (
            <span className="text-muted-foreground text-xs">
              {active?.label} {order.dir === 'asc' ? '↑' : '↓'}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuLabel>Order by</DropdownMenuLabel>
        {ORDER_OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.key}
            // Re-selecting the active field flips direction; a new field starts asc.
            onClick={() =>
              setOrder(
                order.key === o.key && o.key !== 'manual'
                  ? { key: o.key, dir: order.dir === 'asc' ? 'desc' : 'asc' }
                  : { key: o.key, dir: 'asc' },
              )
            }
          >
            <span className="flex-1">{o.label}</span>
            {order.key === o.key ? (
              o.key === 'manual' ? (
                <Check className="size-4" />
              ) : order.dir === 'asc' ? (
                <ArrowUp className="size-3.5" />
              ) : (
                <ArrowDown className="size-3.5" />
              )
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The work-item filter rows in the shared Filters popover (FE-ADR-064). */
function WorkItemFiltersPopover({
  project,
  fil,
  setFil,
  activeFilters,
  onClear,
}: {
  project: Project;
  fil: FilterState;
  setFil: (f: FilterState) => void;
  activeFilters: number;
  onClear: () => void;
}) {
  const states = rowsOf<State>(statesHooks.useList({ project: project.id }).data);
  const labels = rowsOf<LabelModel>(labelsHooks.useList({ project: project.id }).data);
  const modules = rowsOf<Module>(modulesHooks.useList({ project: project.id }).data);
  const cycles = rowsOf<Cycle>(cyclesHooks.useList({ project: project.id }).data);
  // Assignee options are this project's members only — not the whole org roster
  // (assignees can only be project members, so the filter must match).
  const projectMembers = rowsOf<ProjectMember>(
    projectMembersHooks.useList({ project: project.id }).data,
  );
  const set = (k: keyof FilterState, v: string) => setFil({ ...fil, [k]: v });

  return (
    <FiltersPopover activeFilters={activeFilters} onClear={onClear}>
        <FilterSelect
          label="State" value={fil.state} onChange={(v) => set('state', v)} allLabel="All states"
          options={states.map((s) => ({ value: String(s.id), label: s.name }))}
        />
        <FilterSelect
          label="Priority" value={fil.priority} onChange={(v) => set('priority', v)} allLabel="All priorities"
          options={PRIORITIES.map((p) => ({ value: p.value, label: p.label }))}
        />
        <FilterSelect
          label="Assignee" value={fil.assignee} onChange={(v) => set('assignee', v)} allLabel="Anyone"
          options={projectMembers.map((pm) => ({
            value: String(pm.member),
            label: projectMemberName(pm),
          }))}
        />
        <FilterSelect
          label="Label" value={fil.label} onChange={(v) => set('label', v)} allLabel="All labels"
          options={labels.map((l) => ({ value: String(l.id), label: l.name }))}
        />
        <FilterSelect
          label="Module" value={fil.module} onChange={(v) => set('module', v)} allLabel="All modules"
          options={modules.map((m) => ({ value: String(m.id), label: m.name }))}
        />
        <FilterSelect
          label="Cycle" value={fil.cycle} onChange={(v) => set('cycle', v)} allLabel="All cycles"
          options={cycles.map((c) => ({ value: String(c.id), label: c.name }))}
        />
    </FiltersPopover>
  );
}
